import { doc, getDoc, setDoc, deleteField } from "firebase/firestore";
import { auth, db, functions } from "@/services/firebase/firebase";
import { httpsCallable } from "firebase/functions";
import type { Profile, ProfileSectionKey } from "@/types/firebase/profile";

// Re-export types for convenience
export type { Profile } from "@/types/firebase/profile";

const userDocRef = () => doc(db, "users", auth.currentUser!.uid);

export const getProfile = async (): Promise<Profile | undefined> => {
  const snap = await getDoc(userDocRef());
  return snap.exists() ? (snap.data() as Profile) : undefined;
};

export const saveProfileSection = async (
  section: ProfileSectionKey,
  data: Partial<Profile[ProfileSectionKey]>
) => {
  try {
    // Fetch the latest profile
    const latestProfileSnap = await getDoc(userDocRef());
    const latestProfile = latestProfileSnap.exists()
      ? (latestProfileSnap.data() as Profile)
      : {};

    // Merge in the new section
    const updatedProfile = { ...latestProfile, [section]: data };

    // Save the full canonical profile
    await setDoc(userDocRef(), updatedProfile);
  } catch (error) {
    console.error("Error saving profile section:", error);
    throw error;
  }

  // // Trigger search index update
  // debounceSearchIndexUpdate(() => {
  //   triggerSearchIndexUpdate();
  // });
};

// Update profile with arbitrary data (merging)
export const updateProfile = async (data: Partial<Profile>) => {
  await setDoc(userDocRef(), data, { merge: true });
};

// Remove a profile section entirely
export const deleteProfileSection = async (section: string) => {
  await setDoc(userDocRef(), { [section]: deleteField() }, { merge: true });

  // Note: Search index update is now handled separately via debounced trigger
};

let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

export function debounceSearchIndexUpdate(triggerFn: () => void, delay = 500) {
  if (debounceTimeout) clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(() => {
    triggerFn();
    debounceTimeout = null;
  }, delay);
}

export const readLawyerProfileFromSearchIndex = async () => {
  const uid = auth.currentUser!.uid;
  const docRef = doc(db, "searchIndex", "lawyers", "items", uid);
  const snap = await getDoc(docRef);
  return snap.exists();
};

export const toggleProfileVisibility = async (isVisible: boolean) => {
  try {
    const toggleProfileVisibility = httpsCallable(
      functions,
      "toggleProfileVisibility"
    );
    await toggleProfileVisibility({ profileVisible: isVisible });
    
  } catch (error) {
    console.error("❌ Failed to sync lawyer profile to search index:", error);
  }
};
